import Cookies from "js-cookie";
import { Decrypt, EncryptBase64WithIV } from "../utils/common";
import { UserCookie } from "../utils/constants";
import config from "../config.json";

const getCliCookie = () => {
  try {
    const raw = Cookies.get("_cli");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getPortalClientId = () => {
  try {
    const userCookie = Cookies.get(UserCookie.CookieName);
    if (userCookie) {
      const parsed = JSON.parse(userCookie);
      const id = parseInt(parsed?.[UserCookie.ClientId], 10);
      if (id > 0) return id;
    }
    const cli = getCliCookie();
    if (!cli?.id) return 0;
    return parseInt(Decrypt(cli.id), 10) || 0;
  } catch {
    return 0;
  }
};

export const getPortalClientLogo = (loggedinUser) => {
  try {
    if (loggedinUser?.[UserCookie.ClientLogo]) return loggedinUser[UserCookie.ClientLogo];
    return getCliCookie()?.logo || "";
  } catch {
    return "";
  }
};

export const getPortalClientUrl = (loggedinUser) => {
  try {
    if (loggedinUser?.[UserCookie.ClientPortalUrl]) return loggedinUser[UserCookie.ClientPortalUrl];
    return getCliCookie()?.purl || "";
  } catch {
    return "";
  }
};

// True when the page shows a client's branding: a client portal (ClientId > 0) with a client logo.
// Then both headers show only the client logo, and the footers hide the AssetsWatch footer block
// and add "Powered by AssetsWatch" to the copyright line.
export const isClientBranded = (loggedinUser) =>
  getPortalClientId() > 0 && !!getPortalClientLogo(loggedinUser);

// Client display name from _cli (empty when the client has none; callers fall back to Assets Watch).
export const getPortalClientName = () => getCliCookie()?.name || "";

// AW subdomain saved in _cli ("" for direct AssetsWatch).
export const getCliAwSubDomain = () => getCliCookie()?.sub || "";

// Client subdomain of the current host under config.baseDomain, e.g.
// starpropgroup.assetswatch.com -> "starpropgroup". "" on the main domain, www,
// localhost or when baseDomain is not set.
export const getHostAwSubDomain = () => {
  const baseDomain = (config.baseDomain || "").toLowerCase();
  if (!baseDomain) return "";
  const host = window.location.hostname.toLowerCase();
  if (!host.endsWith("." + baseDomain)) return "";
  const sub = host.slice(0, -(baseDomain.length + 1));
  return sub && sub !== "www" && !sub.includes(".") ? sub : "";
};

// https://<awsubdomain>.<baseDomain> ("" when baseDomain is not set).
export const getAwSubDomainOrigin = (sub) =>
  config.baseDomain && sub ? `https://${sub}.${config.baseDomain}` : "";

// Reads the /clientauth/<link> value generated in aw-admin: base64url of
// EncryptBase64WithIV(JSON.stringify({ sub, logo })). Returns { sub, logo } or null.
// logo is only shown in the ClientAuth loader; _cli is built from the API response.
export const decodeClientAuthLink = (value) => {
  try {
    if (!value) return null;
    let b64 = value.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const data = JSON.parse(Decrypt(b64));
    const sub = String(data?.sub || "").trim().toLowerCase();
    if (!/^[a-z0-9-]{1,63}$/.test(sub)) return null;
    const logo = /^https?:\/\//i.test(data?.logo || "") ? data.logo : "";
    return { sub, logo };
  } catch {
    return null;
  }
};

// ClientId held in the _cli cookie only (0 when missing or invalid).
export const getCliClientId = () => {
  try {
    const cli = getCliCookie();
    if (!cli?.id) return 0;
    return parseInt(Decrypt(cli.id), 10) || 0;
  } catch {
    return 0;
  }
};

// ClientId the logged-in user signed in under (0 = direct AssetsWatch login or not logged in).
export const getLoggedInClientId = () => {
  try {
    const userCookie = Cookies.get(UserCookie.CookieName);
    if (!userCookie) return 0;
    return parseInt(JSON.parse(userCookie)?.[UserCookie.ClientId], 10) || 0;
  } catch {
    return 0;
  }
};

// A user who signed in through a client must still have a matching _cli cookie.
// Direct AssetsWatch users (clid 0) never have one and are always valid.
export const isClientSessionValid = () => {
  const loggedInClientId = getLoggedInClientId();
  return loggedInClientId === 0 || getCliClientId() === loggedInClientId;
};

// Saves an incoming client ({ id, logo, purl, pfx, name, sub }) as the _cli cookie. If a user is
// logged in under a different client (or directly), logs them out first so they
// sign in again under the incoming client.
export const setPortalClient = (client, isAuthenticated, logoutUser) => {
  const clientId = parseInt(client?.id, 10) || 0;
  if (clientId <= 0) return;
  if (isAuthenticated() && getLoggedInClientId() !== clientId) {
    logoutUser();
  }
  Cookies.set(
    "_cli",
    JSON.stringify({
      id: EncryptBase64WithIV(String(clientId)),
      logo: client.logo || "",
      purl: client.purl || "",
      pfx: client.pfx || "",
      name: client.name || "",
      sub: client.sub || "",
    }),
    { expires: 30 }
  );
};

// AssetsWatch's own public marketing pages. A leftover client logo is removed only here;
// every other page (sign-in flow, document/invoice links, checkout, ...) keeps _cli
// because client users can open those directly.
const OUTSIDE_PATHS = [
  "/home",
  "/aboutus",
  "/howitworks",
  "/plans",
  "/contactus",
  "/partners",
  "/properties",
  "/property",
  "/agents",
  "/agent",
  "/business",
  "/owner",
];

const isOutsidePath = (pathname) => {
  const path = (pathname || "").toLowerCase().replace(/\/+$/, "");
  if (path === "") return true; // home page "/"
  return OUTSIDE_PATHS.some((p) => path === p || path.startsWith(p + "/"));
};

// Drops a leftover _cli on outside pages so they show only the AssetsWatch logo.
// Kept when logged in under a client or when the URL is bringing a new _cli right now.
export const clearStaleClientCookie = (pathname = window.location.pathname) => {
  // A client subdomain always belongs to its client.
  if (getHostAwSubDomain()) return;
  if (!isOutsidePath(pathname)) return;
  if (getLoggedInClientId() > 0) return;
  try {
    if (new URLSearchParams(window.location.search).has("_cli")) return;
  } catch {
    /* ignore */
  }
  Cookies.remove("_cli", { path: "/" });
};

// Re-saves the existing _cli with the same options as the login cookie (_u) so both
// expire together. No-op when there is no _cli (direct AssetsWatch users).
export const refreshPortalClientCookie = (options) => {
  const raw = Cookies.get("_cli");
  if (!raw) return;
  Cookies.set("_cli", raw, { ...options });
};
